import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment as env } from '../../environments/environment';
import { APIService } from './api.service';
import {
  IntakeAddress, IntakeBuyer, IntakeDeleteResult, IntakeFilter, IntakeMail, IntakeMailListResponse, IntakeUpdateState,
  IntakeVacancyFields,
} from '../interfaces/intake';
import { MarketVacancy } from '../interfaces/market-vacancy';

/** Statuses whose body carries an intake.errors.* key; never toasted raw. */
const SILENT = [409, 422];

/** Statuses whose text is raw Laravel or an intake key; the caller toasts one translated message. */
export const INTAKE_DELETE_SILENT = [403, 404, 409, 422];

/** A positive safe integer, from a number or a digit-only string; anything else is null. */
function positiveId(id: unknown): number | null {
  const n = typeof id === 'string' && /^[0-9]+$/.test(id) ? Number(id) : id;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * The only URL the mail iframe may load. Callers pass the result to
 * bypassSecurityTrustResourceUrl, so it must never carry anything but a number.
 */
export function intakeHtmlPath(mailId: unknown): string | null {
  const id = positiveId(mailId);
  return id === null ? null : `${env.api}/intake/mails/${id}/html`;
}

/** Download link for a stored attachment. Never rendered in the app (spec 2). */
export function intakeAttachmentPath(mailId: unknown, attachmentId: unknown): string | null {
  const m = positiveId(mailId);
  const a = positiveId(attachmentId);
  return m === null || a === null ? null : `${env.api}/intake/mails/${m}/attachments/${a}`;
}

/** Content is gone, or going: no frame, no attachment links, no contacts. */
export function intakeContentGone(mail: { verdict: string; content_wipe_state: string }): boolean {
  return mail.verdict === 'expired' || mail.content_wipe_state !== 'none';
}

/** Toast text for a failed intake call: translate intake.errors.* keys, pass other messages through. */
export function intakeErrorMessage(translate: TranslateService, e: any, passThrough = true): string {
  const message = e?.error?.message;
  if (typeof message === 'string' && message.startsWith('intake.errors.')) {
    return translate.instant(message);
  }
  if (passThrough && typeof message === 'string' && message.trim()) {
    return message;
  }
  return translate.instant('intake.errors.generic');
}

@Injectable({
  providedIn: 'root'
})
export class IntakeService extends APIService {

  getAddress(): Promise<IntakeAddress> {
    return this.get<IntakeAddress>('/intake/address');
  }

  /** The strip also renders for roles that may not read the address: a 403 is an expected answer, null, no toast. */
  addressQuietly(): Promise<IntakeAddress | null> {
    return this.errorify(this.http.get<IntakeAddress>(`${env.api}/intake/address`, this.apiOptions).toPromise(), null, [403])
      .catch((e) => {
        if (e?.status === 403) {
          return null;
        }
        throw e;
      });
  }

  rotateAddress(): Promise<IntakeAddress> {
    return this.post<{}, IntakeAddress>('/intake/address/rotate', {});
  }

  list(filter: IntakeFilter, q: string, page: number, perPage: 25 | 50 | 100): Promise<IntakeMailListResponse> {
    const params = new URLSearchParams({ filter, page: String(page), per_page: String(perPage) });
    const query = (q ?? '').trim();
    if (query) {
      params.set('q', query);
    }
    return this.get<IntakeMailListResponse>(`/intake/mails?${params.toString()}`);
  }

  /** The purified mail HTML as text, for the viewer's srcdoc wrapper. Not toasted: the viewer falls back to the frame URL. */
  mailHtml(id: number): Promise<string> {
    return this.http.get(intakeHtmlPath(id), { ...this.apiOptions, responseType: 'text' as const }).toPromise();
  }

  /** A 404 stays silent: the mail view shows its own not-found message. */
  mail(id: number): Promise<IntakeMail> {
    return this.errorify(this.http.get<IntakeMail>(`${env.api}/intake/mails/${id}`, this.apiOptions).toPromise(), null, [404]);
  }

  /*
   * Every mail action keeps 409 and 422 silent: their `message` is an
   * intake.errors.* key (D1), which errorify would toast raw. The caller
   * toasts intakeErrorMessage() instead (panel run(), dialog inline).
   */
  toDirectRequest(id: number, fields: IntakeVacancyFields): Promise<IntakeMail> {
    return this.post<IntakeVacancyFields, IntakeMail>(`/intake/mails/${id}/direct-request`, fields, null, SILENT);
  }

  notARole(id: number): Promise<IntakeMail> {
    return this.post<{}, IntakeMail>(`/intake/mails/${id}/not-a-role`, {}, null, SILENT);
  }

  parseNow(id: number): Promise<IntakeMail> {
    return this.post<{}, IntakeMail>(`/intake/mails/${id}/parse-now`, {}, null, SILENT);
  }

  release(id: number): Promise<IntakeMail> {
    return this.post<{}, IntakeMail>(`/intake/mails/${id}/release`, {}, null, SILENT);
  }

  retry(id: number): Promise<IntakeMail> {
    return this.post<{}, IntakeMail>(`/intake/mails/${id}/retry`, {}, null, SILENT);
  }

  /** "None, it is a new request" is not a choice here: the panel uses toDirectRequest for it. */
  chooseMatch(id: number, vacancyId: number): Promise<IntakeMail> {
    return this.post<{ vacancy_id: number }, IntakeMail>(`/intake/mails/${id}/choose-match`, { vacancy_id: vacancyId }, null, SILENT);
  }

  updateState(id: number, state: IntakeUpdateState): Promise<IntakeMail> {
    return this.post<{ state: IntakeUpdateState }, IntakeMail>(`/intake/mails/${id}/update-state`, { state }, null, SILENT);
  }

  /** APIService.put has no silent statuses, so errorify is called directly to keep 422 inline. */
  updateVacancy(id: number, fields: Partial<IntakeVacancyFields>): Promise<IntakeMail> {
    return this.errorify(
      this.http.put<IntakeMail>(`${env.api}/intake/mails/${id}/vacancy`, fields, this.apiOptions).toPromise(),
      null,
      SILENT,
    );
  }

  /** 200 `{content_wipe_state}` (wiped or failed); the statuses in INTAKE_DELETE_SILENT stay silent for the caller to translate. */
  deleteMail(id: number): Promise<IntakeDeleteResult> {
    return this.errorify(
      this.http.delete<IntakeDeleteResult>(`${env.api}/intake/mails/${id}`, this.apiOptions).toPromise(),
      null,
      INTAKE_DELETE_SILENT,
    );
  }

  /** At most 50 rows (D1 Task 3), so the picker searches with q instead of filtering locally. */
  buyers(q = ''): Promise<IntakeBuyer[]> {
    const query = (q ?? '').trim();
    return this.get<IntakeBuyer[]>(query ? `/intake/buyers?q=${encodeURIComponent(query)}` : '/intake/buyers');
  }

  /**
   * The linked vacancy, for the Direct request card and the edit prefill (the
   * detail carries only {id, title, needs_details, buyer_Client_id}). A 404 (not
   * visible to this user) is an expected answer here: null, no toast. Other failures toast and reject.
   */
  vacancyQuietly(id: number): Promise<MarketVacancy | null> {
    return this.errorify(this.http.get<MarketVacancy>(`${env.api}/market-vacancies/${id}`, this.apiOptions).toPromise(), null, [404])
      .catch((e) => {
        if (e?.status === 404) {
          return null;
        }
        throw e;
      });
  }
}
